<template>
  <div class="product-mapping mb-5 pt-3">
    <slot name="top"></slot>
    <b-row v-if="showHeader">
      <b-col>
        <div class="d-flex mb-1">
          <span class="mr-2">{{ config.titleLabel }}:</span>
          <div style="font-size: 1.1rem">
            <b class="mr-2">{{ headerText }}</b>
            <span class="text-success">{{ convertPrice(headerPrice) }}</span>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row :class="showHeader ? 'mt-3' : ''">
      <b-col md="12">
        <slot name="search" v-bind="{ disabled, loading }">
          <b-row v-if="config.searchType === 'autosuggest'">
            <b-col cols="3" class="pr-0" v-if="config.showSearchTypeSelect">
              <b-form-select
                :value="searchTypeValue"
                :options="config.searchTypeOptions"
                size="sm"
                value-field="id"
                text-field="name"
                :disabled="searchTypeDisabled"
                class="select-style"
                @input="$emit('update:searchTypeValue', $event)"
              ></b-form-select>
            </b-col>
            <b-col :cols="config.showSearchTypeSelect ? 9 : 12" class="pl-0">
              <vue-autosuggest
                :value="searchText"
                :suggestions="suggestions"
                :get-suggestion-value="getSuggestionValue"
                :limit="10"
                :input-props="{
                  class: 'autosuggest__input form-control form-control-sm',
                  placeholder: config.searchPlaceholder,
                  disabled: disabled,
                }"
                @input="handleSearchInput"
                @selected="handleSelected"
                :should-render-suggestions="shouldRenderSuggestions"
              >
                <template slot-scope="{ suggestion }">
                  <slot name="suggestion" :suggestion="suggestion">
                    <div class="suggestion-item">
                      <span class="text-primary">
                        ({{ convertPrice(suggestion.item.sellingPrice) }})
                      </span>
                      <span
                        v-if="suggestion.item.quantityInStock !== undefined"
                        :class="
                          suggestion.item.quantityInStock > 0
                            ? 'text-success'
                            : 'text-danger'
                        "
                      >
                        (Tồn TCB: {{ suggestion.item.quantityInStock }})
                      </span>
                      {{ suggestion.item.productName || suggestion.item.name }}
                      <span
                        v-if="suggestion.item.imeiCode"
                        class="text-warning"
                      >
                        - IMEI: {{ suggestion.item.imeiCode }}
                      </span>
                    </div>
                  </slot>
                </template>
              </vue-autosuggest>
            </b-col>
          </b-row>
          <b-row v-else-if="config.searchType === 'treeselect'">
            <b-col cols="3" class="pr-0" v-if="config.showSearchTypeSelect">
              <b-form-select
                :value="searchTypeValue"
                :options="config.searchTypeOptions"
                size="sm"
                value-field="id"
                text-field="name"
                :disabled="searchTypeDisabled"
                class="select-style"
                @input="handleSearchTypeChange"
              ></b-form-select>
            </b-col>
            <b-col
              :cols="config.showSearchTypeSelect ? 9 : 12"
              :class="config.showSearchTypeSelect ? 'pl-0' : ''"
            >
              <treeselect
                v-if="treeSelectReady"
                :load-options="config.loadOptions"
                :defaultOptions="config.defaultOptions || []"
                :multiple="false"
                :async="true"
                :placeholder="config.searchPlaceholder"
                :cache-options="false"
                :clearOnSelect="true"
                retryText="Thử lại..."
                searchPromptText="Nhập để tìm kiếm..."
                loadingText="Đang tải..."
                :noResultsText="config.noResultsText"
                openDirection="bottom"
                @select="handleTreeSelectSelected"
                :always-open="isTreeSelectOpen"
                @search-change="handleTreeSelectSearchChange"
                :disabled="disabled"
              >
                <label
                  slot="option-label"
                  slot-scope="{ node, labelClassName }"
                  :class="labelClassName"
                >
                  <slot name="tree-option" :node="node">
                    <div>
                      <b class="text-primary">
                        ({{ convertPrice(node.raw.sellingPrice) }})
                      </b>
                      {{ node.raw.label }}
                    </div>
                  </slot>
                </label>
              </treeselect>
            </b-col>
          </b-row>
        </slot>
        <b-row class="mt-3" v-if="config.showTable">
          <b-col>
            <b-table
              :items="items"
              :fields="tableFields"
              :small="true"
              :busy="loading"
              responsive
              bordered
              hover
            >
              <template #cell(sellingPrice)="data">
                <span v-if="data.value">
                  {{ convertPrice(data.value) }}
                </span>
              </template>

              <template #cell(actions)="data" v-if="config.showActions">
                <slot name="actions" :item="data.item">
                  <i
                    style="font-size: 1rem; color: #d33; cursor: pointer"
                    class="flaticon2-rubbish-bin-delete-button"
                    @click="$emit('remove-item', data.item)"
                  ></i>
                </slot>
              </template>

              <!-- Custom cells slot -->
              <template
                v-for="field in customFields"
                v-slot:[`cell(${field})`]="data"
              >
                <slot :name="`cell-${field}`" :data="data">
                  {{ data.value }}
                </slot>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

export default {
  name: 'GenericSearchTab',
  components: {
    Treeselect,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    searchText: {
      type: String,
      default: '',
    },
    searchTypeValue: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    headerText: {
      type: String,
      default: '',
    },
    headerPrice: {
      type: Number,
      default: null,
    },
    searchTypeDisabled: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => ({}),
      validator: (config) => {
        if (
          config.searchType &&
          !['autosuggest', 'treeselect'].includes(config.searchType)
        ) {
          console.error('searchType must be "autosuggest" or "treeselect"');
          return false;
        }
        return true;
      },
    },
    tableFields: {
      type: Array,
      default: () => [
        {
          key: 'productNameSystem',
          label: 'Tên sản phẩm',
          thClass: 'text-center',
        },
        {
          key: 'barCode',
          label: 'Barcode',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'productCode',
          label: 'Mã sản phẩm',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'sellingPrice',
          label: 'Số tiền',
          thClass: 'text-center',
          tdClass: 'text-right',
        },
        {
          key: 'actions',
          label: '',
          thClass: 'text-center',
          tdClass: 'text-right',
        },
      ],
    },
  },
  data() {
    return {
      isTreeSelectOpen: false,
      treeSelectReady: true,
    };
  },
  computed: {
    mergedConfig() {
      return {
        searchType: 'autosuggest',
        titleLabel: 'Sản phẩm',
        searchPlaceholder: 'Tìm kiếm...',
        showHeader: true,
        showTable: true,
        showActions: true,
        showSearchTypeSelect: false,
        searchTypeOptions: [
          { id: 1, name: 'Tìm sản phẩm' },
          { id: 2, name: 'Tìm sản phẩm imei' },
        ],
        noResultsText: 'Không có kết quả',
        ...this.config,
      };
    },
    showHeader() {
      return this.mergedConfig.showHeader;
    },
    customFields() {
      return this.tableFields
        .filter((f) => !['sellingPrice', 'actions'].includes(f.key))
        .map((f) => f.key);
    },
  },
  methods: {
    convertPrice,
    handleSearchInput(value) {
      this.$emit('update:searchText', value);
      this.$emit('search-input', value);
    },
    handleSelected(option) {
      this.$emit('item-selected', option.item);
    },
    shouldRenderSuggestions(size, loading) {
      return size >= 0 && !loading;
    },
    getSuggestionValue(item) {
      return item.item.productName || item.item.name;
    },
    handleTreeSelectSearchChange(searchQuery) {
      if (searchQuery.length > 1 && !this.isTreeSelectOpen) {
        this.isTreeSelectOpen = true;
      } else if (searchQuery.length === 0) {
        this.isTreeSelectOpen = false;
      }
      this.$emit('search-change', searchQuery);
    },
    handleTreeSelectSelected(node) {
      this.isTreeSelectOpen = false;
      this.$emit('item-selected', node);
    },
  },
};
</script>

<style scoped>
.suggestion-item {
  padding: 0.25rem 0;
}
</style>
<style>
:root {
  --input-height-sm: 31px;
  --input-font-sm: 0.875rem;
  --input-padding-sm: 0.25rem 0.5rem;
}

.product-mapping .select-style,
.product-mapping .autosuggest__input,
.product-mapping .vue-treeselect__control {
  height: var(--input-height-sm) !important;
  min-height: var(--input-height-sm) !important;
  font-size: var(--input-font-sm) !important;
}

.vue-treeselect__placeholder,
.vue-treeselect__single-value,
.vue-treeselect__input {
  line-height: calc(var(--input-height-sm) - 2px) !important;
  font-size: var(--input-font-sm) !important;
}

.vue-treeselect__input-container,
.vue-treeselect__value-container {
  padding: 0 8px !important;
}

.autosuggest__input {
  padding: var(--input-padding-sm) !important;
  line-height: 1.5 !important;
}
</style>
